import { FC } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

import UntitledIcon from '@payaca/untitled-icons';

import { clstx } from '@payaca/utilities/cvaMerge';
import Button from '../Button/Button';
import Conditional from '../Conditional/Conditional';
import UntitledLoading from '../UntitledLoading/UntitledLoading';

export interface FileInputProps {
  dropzoneOptions?: Omit<DropzoneOptions, 'onDrop' | 'maxFiles' | 'multiple'>;
  onDrop?: (file: File) => void;
  onDelete?: () => void;
  url?: string;
  label: string;
  isLoading?: boolean;
}

const FileInput: FC<FileInputProps> = (props) => {
  const { dropzoneOptions, onDrop, url, label, onDelete, isLoading } = props;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    multiple: false,
    onDrop: (acceptedFiles) => {
      const acceptedFile = acceptedFiles[0];

      if (!acceptedFile) {
        return;
      }

      onDrop?.(acceptedFile);
    },
    disabled: !!isLoading,
    ...dropzoneOptions,
  });

  return (
    <div className="typography:prose flex items-center gap-3">
      <div className="flex items-center gap-3" {...getRootProps()}>
        <div
          className={clstx(
            'flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg bg-white',
            !url && 'border border-dashed border-gray-200',
            isDragActive && 'border-blue-600 bg-blue-50'
          )}
        >
          <Conditional condition={!url && !isLoading}>
            <UntitledIcon
              name="image-01.3"
              className="non-scaling-stroke h-7 w-7"
            />
          </Conditional>

          <Conditional condition={isLoading}>
            <UntitledLoading className="h-7 w-7" />
          </Conditional>

          {url && <img src={url} alt={label} />}
        </div>

        <input {...getInputProps()} />

        <Button size={'sm'} variant="secondary">
          <UntitledIcon name="upload-01.3" className="h-4 w-4" />
          {url ? 'Replace' : 'Upload'}
        </Button>
      </div>
      <Conditional condition={!!url && !!onDelete}>
        <Button size={'sm'} variant="secondary" onClick={onDelete}>
          Remove
        </Button>
      </Conditional>
    </div>
  );
};

export default FileInput;
